import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import type { ChannexPublicationResponse, ChannexReconcileAction } from '@/shared/api';
import {
  allSelectableChosen,
  effectiveSelection,
  isSettling,
  nextAction,
  selectableUnits,
} from './publicationModel';
import type { LifecycleAction, PublishAction } from './publicationModel';
import type { ReconcileTarget } from './ReconcileBindingDialog';

/** Solange etwas läuft, fragt die Fläche nach — sonst bliebe sie stehen, bis jemand neu lädt. */
const SETTLING_POLL_MS = 5_000;

export function usePublicationCentre(groupId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const queryKey = ['property-groups', groupId, 'publication'];

  const query = useQuery({
    queryKey,
    queryFn: () => api.channex.publicationState(groupId),
    refetchInterval: (q) => {
      const data = q.state.data as ChannexPublicationResponse | undefined;
      return data && isSettling(data.parentState, data.units) ? SETTLING_POLL_MS : false;
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey });
  // Eine Ablehnung ist der Moment, in dem die Fläche am weitesten vom Server entfernt ist —
  // gerade dann muss sie nachladen, sonst bietet sie denselben Fehlschlag erneut an.
  const fail = async (error: unknown) => {
    const parsed = await toApiError(error);
    toast.error(parsed.detail ?? parsed.title);
    await refresh();
  };

  const data = query.data;
  const units = data?.units ?? [];
  const selectable = selectableUnits(units);
  const effective = effectiveSelection(selected, units);
  const action: PublishAction = data ? nextAction(data.parentState, effective.length) : 'none';

  const publish = useMutation({
    mutationFn: async (target: Exclude<PublishAction, 'none'>) =>
      target === 'parent'
        ? api.channex.publishObjectParent(groupId)
        : api.channex.publishObjectUnits(groupId, effective),
    onSuccess: async () => {
      setSelected([]);
      toast.success('Veröffentlichung eingeplant.');
      await refresh();
    },
    onError: fail,
  });

  const lifecycle = useMutation({
    mutationFn: (variables: { propertyId: string; action: NonNullable<LifecycleAction> }) =>
      variables.action === 'pause'
        ? api.properties.pause(variables.propertyId)
        : api.properties.activate(variables.propertyId),
    onSuccess: async (_result, variables) => {
      toast.success(
        variables.action === 'pause'
          ? 'Apartment pausiert — der Kanalmanager schließt es.'
          : 'Apartment fortgesetzt — freie Nächte gehen wieder auf.',
      );
      await refresh();
    },
    onError: fail,
  });

  /**
   * Der manuelle Abgleich: PlatformAdmin-only und bewusst ohne „nochmal versuchen". Entweder wir
   * uebernehmen die Kennung einer bereits existierenden Vendor-Ressource, oder wir bestaetigen,
   * dass dort nichts liegt — ein zweiter Create waere ein Duplikat (§6.102).
   */
  const reconcile = useMutation({
    mutationFn: (variables: {
      target: ReconcileTarget;
      action: ChannexReconcileAction;
      remoteId?: string;
    }) =>
      variables.target.scope === 'parent'
        ? api.channex.reconcileParentBinding(variables.target.bindingId, {
            action: variables.action,
            remoteId: variables.remoteId,
          })
        : api.channex.reconcileUnitBinding(variables.target.bindingId, {
            action: variables.action,
            resource: variables.target.resource ?? 'RoomType',
            remoteId: variables.remoteId,
          }),
    onSuccess: async (_result, variables) => {
      toast.success(
        variables.action === 'attach'
          ? 'Kennung übernommen — die Bindung ist wieder frei.'
          : 'Als nicht vorhanden bestätigt — die Veröffentlichung legt sie neu an.',
      );
      await refresh();
    },
    onError: fail,
  });

  return {
    query,
    data,
    selectable,
    effective,
    action,
    allChosen: allSelectableChosen(selected, selectable),
    toggle: (propertyId: string) =>
      setSelected((current) =>
        current.includes(propertyId)
          ? current.filter((id) => id !== propertyId)
          : [...current, propertyId],
      ),
    toggleAll: () => setSelected(allSelectableChosen(selected, selectable) ? [] : selectable),
    publish,
    lifecycle,
    reconcile,
  };
}
